import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
} from '../components/ui.js';
import { Badge as Pill } from '../components/2b-status.js';
import { dateTime } from '../lib/format.js';
import { CHANNEL_LABEL, WINDOW_LABEL, label } from '../lib/labels.js';
import type { Rights } from '../lib/session.js';
import '../screens-2b.css';

/**
 * Editor flow (SPEC-2 kap. 12.2). Pravidla jsou na časové ose od kotvy:
 * posun se mění číslem i tažením jezdce, podmínky se vybírají ze seznamu —
 * žádné volné výrazy, které by engine neuměl.
 */

interface Rule {
  code: string;
  name: string;
  channel: 'marketing' | 'service';
  sender: 'hub' | 'eshop';
  scope: string;
  /** Chybí u pravidel, která posílá e-shop; u událostí může být i seznam. */
  anchor?: string | string[];
  offset?: string;
  valid_for?: string;
  window?: string;
  conditions?: string[];
  merge_group?: string;
  /** Minimální předstih cesty — brání zprávám na poslední chvíli. */
  min_lead_days?: number;
  template_key?: string;
}

interface CatalogueDoc {
  defaults?: Record<string, unknown>;
  merge_groups?: Record<string, unknown>;
  rules: Rule[];
}

interface JourneyStep {
  code: string;
  name: string;
  channel: 'marketing' | 'service';
  sender: 'hub' | 'eshop';
  daysFromPurchase: number | null;
  daysBeforeRace: number | null;
  included: boolean;
  reason: 'too_late' | 'min_lead' | 'needs_date' | null;
  explanation: string;
  catchUp: boolean;
}

interface CatalogueResponse {
  versions: Array<{
    version: number;
    status: string;
    note: string | null;
    publishedAt: string | null;
  }>;
  published: CatalogueDoc | null;
  publishedVersion: number | null;
  draft: CatalogueDoc | null;
  draftVersion: number | null;
  anchors: string[];
}

/** Posun v dnech — pro osu i pro jezdec. Hodiny se počítají jako zlomek dne. */
function offsetDays(offset: string | undefined): number {
  if (!offset || offset === '0') return 0;
  const match = /^([+-]?)(\d+)\s*([dh])$/.exec(offset.trim());
  if (!match) return 0;
  const sign = match[1] === '-' ? -1 : 1;
  const value = Number(match[2]);
  return sign * (match[3] === 'd' ? value : value / 24);
}

const toOffset = (days: number): string =>
  days === 0 ? '0' : `${days > 0 ? '+' : '-'}${Math.abs(days)}d`;

/**
 * Faze cesty zakaznika. Kotva sama o sobe neni faze — `race_start -60d`
 * a `race_start -1d` jsou uplne jine okamziky cesty.
 */
const PHASES = [
  { key: 'nakup', label: 'Hned po nákupu', hint: 'potvrzení, rezervace, co bude dál' },
  { key: 'priprava', label: 'Příprava cesty', hint: 'milníky, Flexi, plánování' },
  { key: 'pred', label: 'Před závodem', hint: 'stav vstupenek, upgrade, co s sebou' },
  { key: 'tyden', label: 'Závodní týden', hint: 'praktické informace, race pack' },
  { key: 'po', label: 'Po závodě', hint: 'hodnocení, příští ročník' },
  { key: 'darky', label: 'Dárky', hint: 'předání a platnost' },
  { key: 'watchdog', label: 'Hlídací pes', hint: 'termín, dostupnost, nový ročník' },
  { key: 'eshop', label: 'Posílá e-shop', hint: 'hub je jen loguje' },
] as const;

type PhaseKey = (typeof PHASES)[number]['key'];

function phaseOf(rule: Rule): PhaseKey {
  if (rule.sender === 'eshop') return 'eshop';
  if (rule.scope === 'gift') return 'darky';
  if (rule.scope === 'watchdog') return 'watchdog';

  const anchor = anchorKey(rule.anchor);
  const days = offsetDays(rule.offset);

  if (anchor === 'review_due' || anchor === 'race_end') return 'po';
  if (anchor === 'race_start') {
    if (days >= -7) return 'tyden';
    if (days >= -45) return 'pred';
    return 'priprava';
  }
  if (anchor === 'order_paid') return days <= 2 ? 'nakup' : 'priprava';
  if (anchor === 'flexi_offer_due') return 'priprava';
  if (anchor.startsWith('event:')) return 'priprava';
  return 'priprava';
}

const ANCHOR_LABELS: Record<string, string> = {
  order_paid: 'Zaplacení objednávky',
  race_start: 'Začátek závodu',
  race_end: 'Konec závodu',
  flexi_offer_due: 'Nabídka Flexi',
  gift_deliver_at: 'Předání dárku',
  gift_expires_at: 'Konec platnosti dárku',
  review_due: 'Hodnocení',
};

/** Pravidla, ktera posila e-shop, kotvu nemaji — hub je jen loguje. */
const NO_ANCHOR = '—';

/** Kotva může být i seznam událostí — pro zobrazení z ní uděláme text. */
const anchorKey = (anchor: string | string[] | undefined): string => {
  if (Array.isArray(anchor)) return anchor[0] ?? NO_ANCHOR;
  return anchor ?? NO_ANCHOR;
};

/** 1 den, 2–4 dny, 5 a víc dní. */
const dny = (n: number): string => {
  if (n === 1) return '1 den';
  if (n >= 2 && n <= 4) return `${n} dny`;
  return `${n} dní`;
};

const VERSION_STATUS: Record<string, string> = {
  published: 'publikovaná',
  draft: 'koncept',
  archived: 'dřívější verze',
};

const anchorLabel = (anchor: string): string => {
  if (anchor === NO_ANCHOR) return 'Bez vztažného bodu — posílá e-shop';
  return (
    ANCHOR_LABELS[anchor] ?? (anchor.startsWith('event:') ? `Událost: ${anchor.slice(6)}` : anchor)
  );
};

export function Flow({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const canEdit = rights.can('edit_catalogue');
  const canPublish = rights.can('publish_catalogue');

  const query = useQuery({
    queryKey: ['flow-catalogue'],
    queryFn: () => api.get<CatalogueResponse>('/api/catalogue'),
  });

  const [doc, setDoc] = useState<CatalogueDoc | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [impact, setImpact] = useState<Array<{
    code: string;
    before: number;
    after: number;
    diff: number;
  }> | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [leadDays, setLeadDays] = useState(7);
  /* Publikace a návrat na starší verzi mění, podle čeho hub plánuje, proto se potvrzují. */
  const [askPublish, setAskPublish] = useState(false);
  const [askRevert, setAskRevert] = useState<number | null>(null);

  /** Co dostane zákazník, který koupí `leadDays` dní před závodem. */
  const journey = useQuery({
    queryKey: ['journey', leadDays],
    queryFn: () => api.get<{ steps: JourneyStep[] }>(`/api/catalogue/journey?leadDays=${leadDays}`),
  });

  useEffect(() => {
    if (!query.data) return;
    setDoc(query.data.draft ?? query.data.published);
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => api.put('/api/catalogue', { doc }),
    onSuccess: () => {
      setNote('Koncept uložen. Do provozu se dostane až publikací.');
      setIssues([]);
      void client.invalidateQueries({ queryKey: ['flow-catalogue'] });
    },
    onError: (err: Error) => setIssues([err.message]),
  });

  const simulate = useMutation({
    mutationFn: () =>
      api.post<{ rows: Array<{ code: string; before: number; after: number; diff: number }> }>(
        '/api/catalogue/simulate',
        {},
      ),
    onSuccess: (data) => {
      setImpact(data.rows.filter((r) => r.diff !== 0));
      setNote(null);
    },
    onError: (err: Error) => setIssues([err.message]),
  });

  const publish = useMutation({
    mutationFn: () => api.post('/api/catalogue/publish', {}),
    onSuccess: () => {
      setNote('Publikováno. Hub plánuje podle nové verze.');
      setImpact(null);
      void client.invalidateQueries({ queryKey: ['flow-catalogue'] });
    },
    onError: (err: Error) => setIssues([err.message]),
  });

  const revert = useMutation({
    mutationFn: (version: number) => api.post('/api/catalogue/revert', { version }),
    onSuccess: () => {
      setNote('Vráceno na dřívější verzi.');
      void client.invalidateQueries({ queryKey: ['flow-catalogue'] });
    },
  });

  /**
   * Kód zprávy není jedinečný — M03 je milník T−270, T−180 i T−90. Pravidlo
   * se proto drží podle pořadí v dokumentu, jinak by úprava jednoho změnila
   * všechna se stejným kódem.
   */
  const groups = useMemo(() => {
    const out = new Map<PhaseKey, Array<{ rule: Rule; index: number }>>();
    (doc?.rules ?? []).forEach((rule, index) => {
      const key = phaseOf(rule);
      const list = out.get(key) ?? [];
      list.push({ rule, index });
      out.set(key, list);
    });
    return PHASES.filter((phase) => (out.get(phase.key) ?? []).length > 0).map((phase) => ({
      ...phase,
      rules: [...(out.get(phase.key) ?? [])].sort(
        (a, b) => offsetDays(a.rule.offset) - offsetDays(b.rule.offset),
      ),
    }));
  }, [doc]);

  if (query.isLoading) return <Loading what="katalog" />;
  if (query.isError)
    return (
      <ErrorState
        title="Tok zpráv se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  if (!doc || !Array.isArray(doc.rules)) {
    return (
      <EmptyState title="Katalog zatím není">
        Publikovaná verze katalogu zpráv chybí, takže není co zobrazit.
      </EmptyState>
    );
  }

  const updateRule = (index: number, patch: Partial<Rule>): void =>
    setDoc({
      ...doc,
      rules: doc.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    });

  return (
    <>
      <PageHeader
        level={2}
        title="Tok zpráv"
        description="Pravidla, která určují, kdy a komu se která zpráva vyhodnotí. Změny se nejdřív ukládají jako koncept a do provozu jdou až publikací."
      />
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}
      {issues.length > 0 ? (
        <div className="confirm dngc" role="alert">
          <b>Nepovedlo se:</b>
          <ul>
            {issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <Panel
        title="Verze a publikace"
        action={
          <div className="row">
            <Pill tone="ok">publikovaná verze {query.data?.publishedVersion ?? '—'}</Pill>
            {query.data?.draftVersion ? <Pill tone="warn">koncept</Pill> : null}
            <a className="btn" href="/api/catalogue/export">
              Stáhnout jako soubor (YAML)
            </a>
            {canEdit ? (
              <>
                <button className="btn" onClick={() => save.mutate()}>
                  Uložit koncept
                </button>
                <button className="btn" onClick={() => simulate.mutate()}>
                  Simulovat dopad
                </button>
              </>
            ) : null}
            {canPublish ? (
              <button type="button" className="btn pri" onClick={() => setAskPublish(true)}>
                Publikovat
              </button>
            ) : null}
          </div>
        }
      >
        <div className="help">
          <p>
            <b>Tahle obrazovka řídí odesílání.</b> Každé pravidlo říká, <b>kdy</b> se zpráva
            vyhodnotí (vztažný bod a posun), <b>komu</b> (podmínky) a <b>jak dlouho</b> ještě smí
            odejít (platnost). Vztažný bod je událost, od které se čas počítá, například zaplacení
            objednávky nebo začátek závodu.
          </p>
          <p>
            Změna se ukládá jako <b>koncept</b>. Hub plánuje dál podle publikované verze, dokud ji
            někdo nepublikuje. Před publikací si nechte spočítat <b>dopad</b>: ukáže, kolik zpráv po
            změně přibude nebo ubude.
          </p>
        </div>

        {impact ? (
          <div className="hint">
            <b>Dopad konceptu na současný plán:</b>
            {impact.length === 0 ? (
              <p className="note">Žádná změna v počtu naplánovaných zpráv.</p>
            ) : (
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Kód</th>
                      <th className="n">Teď</th>
                      <th className="n">Po změně</th>
                      <th className="n">Rozdíl</th>
                    </tr>
                  </thead>
                  <tbody>
                    {impact.map((row) => (
                      <tr key={row.code}>
                        <td>{row.code}</td>
                        <td className="n">{row.before}</td>
                        <td className="n">{row.after}</td>
                        <td className="n">
                          {row.diff > 0 ? '+' : ''}
                          {row.diff}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </Panel>

      <Panel title="Co dostane zákazník podle předstihu">
        <div className="help">
          <p>
            Nejčastější obava: <b>zákazník koupí pár dní před závodem</b> a dostane „plánujte cestu“
            nebo milník T−270. Hub to hlídá dvěma pravidly — zprávu, jejíž termín už proběhl, nikdy
            neposílá zpětně, a pravidlo s minimálním předstihem se při kratším předstihu vůbec
            nepoužije.
          </p>
          <p>Posuňte jezdec a uvidíte, co takový zákazník dostane a co ne — i s důvodem.</p>
        </div>

        <label className="field">
          <span>Koupil {dny(leadDays)} před závodem</span>
          <input
            type="range"
            min={0}
            max={365}
            value={leadDays}
            onChange={(e) => setLeadDays(Number(e.target.value))}
          />
        </label>
        <div className="row">
          {[1, 3, 7, 30, 90, 270].map((d) => (
            <button
              key={d}
              type="button"
              className={`btn${leadDays === d ? ' pri' : ''}`}
              aria-pressed={leadDays === d}
              onClick={() => setLeadDays(d)}
            >
              {dny(d)}
            </button>
          ))}
        </div>

        {journey.isLoading ? (
          <Loading what="náhled" />
        ) : (
          <div className="grid2">
            <div>
              <h4 className="mini">Dostane</h4>
              <ul className="steps">
                {(journey.data?.steps ?? [])
                  .filter((step) => step.included && step.sender === 'hub')
                  .map((step, i) => (
                    <li key={`${step.code}-${i}`}>
                      <span className={`dot${step.channel === 'marketing' ? ' mkt' : ''}`} />
                      <b>{step.code}</b> {step.name}
                      <span className="note">
                        {step.catchUp
                          ? ' — hned po nákupu (dobíhá)'
                          : step.daysFromPurchase === null
                            ? ' — podle události'
                            : ` — ${dny(step.daysFromPurchase)} po nákupu`}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
            <div>
              <h4 className="mini">Nedostane — a proč</h4>
              <ul className="steps muted">
                {(journey.data?.steps ?? [])
                  .filter((step) => !step.included)
                  .map((step, i) => (
                    <li key={`${step.code}-${i}`}>
                      <b>{step.code}</b> {step.name}
                      <span className="note"> — {step.explanation}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Mapa cesty">
        <div className="help">
          <p>
            Celá cesta zákazníka v jednom obrázku. Řádek je fáze cesty, pozice v řádku odpovídá
            posunu od vztažného bodu: vlevo dřív, vpravo později.
          </p>
          <p className="legend">
            <span className="dot" /> servisní zpráva (jde i bez marketingového souhlasu)
            <span className="dot mkt" /> marketing (vyžaduje souhlas a počítá se do frekvenčního
            limitu)
          </p>
        </div>
        <JourneyMap groups={groups} selected={selected} onSelect={setSelected} />
      </Panel>

      {groups.map((group) => (
        <Panel key={group.key} title={`${group.label} — ${group.hint}`}>
          {group.rules.map((item) => (
            <RuleRow
              key={item.index}
              rule={item.rule}
              disabled={!canEdit}
              open={selected === item.index}
              onToggle={() => setSelected(selected === item.index ? null : item.index)}
              onChange={(patch) => updateRule(item.index, patch)}
            />
          ))}
        </Panel>
      ))}

      <Panel title="Verze">
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Verze</th>
                <th>Stav</th>
                <th>Poznámka</th>
                <th>Publikováno</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(query.data?.versions ?? []).map((v) => (
                <tr key={v.version}>
                  <td>{v.version}</td>
                  <td>{label(VERSION_STATUS, v.status)}</td>
                  <td>{v.note ?? '—'}</td>
                  <td>{v.publishedAt ? dateTime(v.publishedAt) : '—'}</td>
                  <td>
                    {canPublish && v.status === 'archived' ? (
                      <button
                        type="button"
                        className="btn"
                        aria-label={`Vrátit verzi ${v.version}`}
                        onClick={() => setAskRevert(v.version)}
                      >
                        Vrátit tuhle
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <ConfirmDialog
        open={askPublish}
        title="Publikovat koncept?"
        confirmLabel="Publikovat"
        busy={publish.isPending}
        onCancel={() => setAskPublish(false)}
        onConfirm={() => {
          setAskPublish(false);
          publish.mutate();
        }}
      >
        <p>
          Hub začne plánovat zprávy podle konceptu. Už naplánované zprávy se mohou změnit. Jestli
          zprávy opravdu odejdou zákazníkům, určuje režim odesílání a vypínače zpráv. Předchozí
          verzi jde později vrátit.
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        open={askRevert !== null}
        title={`Vrátit verzi ${askRevert ?? ''}?`}
        confirmLabel="Vrátit tuhle verzi"
        busy={revert.isPending}
        onCancel={() => setAskRevert(null)}
        onConfirm={() => {
          if (askRevert !== null) revert.mutate(askRevert);
          setAskRevert(null);
        }}
      >
        <p>Tato dřívější verze se znovu publikuje a hub podle ní hned začne plánovat zprávy.</p>
      </ConfirmDialog>
    </>
  );
}

/**
 * Mapa cesty: fáze pod sebou, uvnitř fáze zprávy podle posunu od kotvy.
 * Jedna obrazovka, ze které je vidět, kdy zákazník co dostane.
 */
function JourneyMap({
  groups,
  selected,
  onSelect,
}: {
  groups: Array<{ key: string; label: string; rules: Array<{ rule: Rule; index: number }> }>;
  selected: number | null;
  onSelect: (index: number) => void;
}): JSX.Element {
  return (
    <div className="journey">
      {groups.map((group) => {
        const values = group.rules.map((r) => offsetDays(r.rule.offset));
        const min = Math.min(0, ...values);
        const max = Math.max(0, ...values);
        const span = max - min || 1;
        return (
          <div className="journey-lane" key={group.key}>
            <div className="journey-label">{group.label}</div>
            <div className="journey-track">
              <div className="journey-line" />
              {group.rules.map((item, position) => {
                const left = ((offsetDays(item.rule.offset) - min) / span) * 92 + 4;
                /* Dva kody na stejnem miste by se prekryly — stridame patra. */
                const previous =
                  position > 0
                    ? ((offsetDays(group.rules[position - 1]?.rule.offset) - min) / span) * 92 + 4
                    : -99;
                const row = left - previous < 8 ? 1 : 0;
                return (
                  <button
                    key={item.index}
                    type="button"
                    ref={(el) => {
                      el?.style.setProperty('--x', `${left}%`);
                    }}
                    className={`journey-node s2b-node${row === 1 ? ' low' : ''}${
                      item.rule.channel === 'marketing' ? ' mkt' : ''
                    }${selected === item.index ? ' sel' : ''}`}
                    aria-pressed={selected === item.index}
                    title={`${item.rule.name} (${item.rule.offset ?? '0'} od ${anchorLabel(
                      anchorKey(item.rule.anchor),
                    )})`}
                    onClick={() => onSelect(item.index)}
                  >
                    {item.rule.code}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <p className="note">Klikněte na kód a otevře se jeho nastavení níž.</p>
    </div>
  );
}

const WINDOWS = ['standard', 'critical', 'immediate'];

function RuleRow({
  rule,
  disabled,
  open,
  onToggle,
  onChange,
}: {
  rule: Rule;
  disabled: boolean;
  open: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<Rule>) => void;
}): JSX.Element {
  const days = offsetDays(rule.offset);

  return (
    <div className={`block${open ? ' sel' : ''}`}>
      <div className="row">
        <button
          type="button"
          className="btn"
          aria-expanded={open}
          aria-label={`${open ? 'Skrýt' : 'Upravit'} pravidlo ${rule.code} ${rule.name}`}
          onClick={onToggle}
        >
          {open ? '−' : '+'}
        </button>
        <b>
          {rule.code} — {rule.name}
        </b>
        <span className="note">
          {rule.offset ?? '0'} od: {anchorLabel(anchorKey(rule.anchor))}
          {rule.min_lead_days ? ` · jen s předstihem ${rule.min_lead_days}+ dní` : ''}
        </span>
        <Pill tone={rule.channel === 'marketing' ? 'warn' : 'ok'}>
          {label(CHANNEL_LABEL, rule.channel)}
        </Pill>
        {rule.sender === 'eshop' ? <Pill tone="neu">posílá e-shop</Pill> : null}
        {rule.merge_group ? <Pill tone="neu">sloučí se (skupina {rule.merge_group})</Pill> : null}
      </div>
      {!open ? null : (
        <div className="row">
          <label className="field">
            <span title="Záporný posun znamená „před vztažným bodem“, kladný „po něm“.">
              Posun (dny od vztažného bodu)
            </span>
            <input
              type="range"
              min={-365}
              max={365}
              value={days}
              disabled={disabled}
              onChange={(e) => onChange({ offset: toOffset(Number(e.target.value)) })}
            />
          </label>
          <label className="field">
            <span>Posun</span>
            <input
              value={rule.offset ?? '0'}
              disabled={disabled}
              onChange={(e) => onChange({ offset: e.target.value })}
            />
          </label>
          <label className="field">
            <span title="Jak dlouho po termínu smí zpráva ještě odejít. Potom propadne.">
              Platnost
            </span>
            <input
              value={rule.valid_for ?? ''}
              disabled={disabled}
              onChange={(e) => onChange({ valid_for: e.target.value })}
            />
          </label>
          <label className="field">
            <span title="Denní doba, kdy zpráva smí odejít, v časovém pásmu příjemce.">
              Vhodný čas odeslání
            </span>
            <select
              value={rule.window ?? 'standard'}
              disabled={disabled}
              onChange={(e) => onChange({ window: e.target.value })}
            >
              {WINDOWS.map((w) => (
                <option key={w} value={w}>
                  {label(WINDOW_LABEL, w)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {open && rule.conditions && rule.conditions.length > 0 ? (
        <p className="note">Podmínky: {rule.conditions.join(', ')}</p>
      ) : null}
    </div>
  );
}
